<template>
  <teleport to="body">
    <div
      v-show="isModalVisible"
      class="fixed inset-0 z-30 flex items-center justify-center bg-transparent"
    >
      <div class="fixed top-0 z-10 h-full w-full bg-dark-gray bg-opacity-50" />
      <div
        class="relative z-10 max-w-lg scroll-auto rounded-md bg-white p-4 md:p-6 lg:p-8"
      >
        <div class="flex items-center justify-between">
          <h3 class="text-left text-xl text-dark-blue md:text-2xl lg:text-3xl">
            {{ modalTitle }}
          </h3>
          <img
            class="mt-5"
            src="@/assets/highway-badge.svg"
            alt="Highway Badge"
          />
        </div>
        <form
          novalidate="novalidate"
          @submit.prevent="handleSubmit"
        >
          <div
            v-if="isPending"
            class="my-5 flex flex-col gap-5 text-left"
          >
            <p class="text-dark-gray">
              We just sent an email to
              {{ store.state.users.currentUser?.email || '' }} so you can verify
              your email address. Click on the link in that email and then
              return here to continue.
            </p>
            <p class="text-xs text-body-color">
              Email didn't sent?
              <span
                class="cursor-pointer text-yellow-500 underline hover:text-yellow-600"
                @click="handleResendEmail"
                >Try again</span
              >.
            </p>
          </div>
          <div v-else>
            <p
              class="-mt-3 mb-6 text-left text-dark-gray sm:text-sm md:text-base"
            >
              Let’s complete your profile.
            </p>
            <div>
              <div class="flex flex-col gap-2 text-left">
                <label
                  class="w-full text-left text-2xs font-bold uppercase text-dark-blue"
                  >company email</label
                >
                <p class="mb-1 text-body-color">
                  {{ companyEmail }}
                </p>
                <p class="my-2 text-xs text-body-color">
                  If this email is incorrect, please contact Highway support at
                  <a
                    class="text-yellow-500 underline"
                    href="mailto:service@highway.com"
                  >
                    service@highway.com</a
                  >.
                </p>
              </div>
            </div>
            <section class="flex w-full justify-end">
              <div class="flex w-1/2 justify-end">
                <button-main
                  class="mb-4 mt-3"
                  :disabled="false"
                  type="submit"
                  :loading-spinner="isLoading"
                  value="Continue"
                />
              </div>
            </section>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script setup>
import { useStore } from 'vuex';
import { ref, defineProps, computed, onBeforeUnmount } from 'vue';
import ButtonMain from '@/components/Button.vue';
import { useToast } from 'vue-toast-notification';

defineProps({
  visible: Boolean,
});

const toast = useToast();
const store = useStore();
const isPending = ref(false);
const isLoading = ref(false);
const pollingInterval = ref(null);

const companyEmail = computed(() => store.state.users.currentUser?.email);

const modalTitle = computed(() => {
  return isPending.value
    ? 'Email Verification Pending...'
    : 'Confirm your information';
});

const isModalVisible = computed(
  () => store.state.identityVerification?.isCompanyEmailModalActive
);

const handleSubmit = async () => {
  isPending.value = true;
  const { status } = await store.dispatch(
    'users/updateEmail',
    store.state.users.currentUser.email
  );
  if ([200, 201].includes(status)) {
    activePendingScreen();
  }
};

const activePendingScreen = () => {
  isPending.value = true;
  startPolling();
};

const startPolling = () => {
  pollingInterval.value = setInterval(() => {
    store.dispatch('users/serverRefreshCurrentUser').then(() => {
      if (store.state.users.currentUser.is_verified_email) {
        clearPolling();
        emailValidated();
      }
    });
  }, 5000);
};

const clearPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
  }
};

onBeforeUnmount(() => {
  clearPolling();
});

const emailValidated = () => {
  store.commit('identityVerification/setIsCompanyEmailModal', false);
};
const handleResendEmail = async () => {
  try {
    const res = await store.dispatch(
      'users/updateEmail',
      store.state.users.currentUser.email
    );

    if ([200, 201, 204].includes(res.status)) {
      toast?.open({
        type: 'info',
        message: 'Email resent',
        position: 'top-right',
        duration: 5000,
      });
    }
  } catch (error) {
    console.error('Error sending validation email:', error);
    toast?.open({
      type: 'error',
      message: 'An error occurred. Please try again.',
      position: 'top-right',
      duration: 5000,
    });
  }
};
</script>
